import "./searchPanel.scss";

import { Map } from "immutable";
import PropTypes from "prop-types";
import PubSub from "pubsub-js";
import React from "react";
import ImmutablePropTypes from "react-immutable-proptypes";
import { connect } from "react-redux";

import { withContainerContext } from "../../components/container/ContainerContext";
import { asDropTarget } from "../../components/dragAndDrop/DroppableComponent";
import EmptyState from "../../components/emptyState";
import TabLock from "../../components/tabLock";
import { SEARCH_RESULTS_VIEW } from "../../constants/identifiers";
import PubSubTopics from "../../constants/pubSubTopics";
import searchableFields from "../../constants/searchableFields";
import { appStateOperations, appStateSelectors } from "../../modules/appState";
import batchUtils from "../../modules/batchUtils";
import { definitionSelectors } from "../../modules/definition";
import { typeSelectors } from "../../modules/definition/types";
import { itemOperations } from "../../modules/items";
import { uiStateOperations, uiStateSelectors } from "../../modules/uiState";
import { immutableRecords, PropTypeShapes } from "../../types";
import { mergeDispatchToProps } from "../../utils/codeSplit";
import SearchPanelHeader from "./SearchPanelHeader";
import SearchResultList from "./SearchResultList";
import selectors from "./selectors";
const SEARCH_LIST_VIEW_ID = "search_list";

const allSearchableFields = Map(searchableFields);

const dropCallbacks = {
  onEnter: (_props, _dragItem, _dropTarget) => {},

  onLeave: (_props, _dragItem, _dropTarget) => {},

  getDragMultipleItemIds(props, dragItem, _dropTarget) {
    return props.selectedItemIds.contains(dragItem.itemId)
      ? props.matchingItems
        .filter(obj => props.selectedItemIds.contains(obj.id))
        .map(obj => obj.id)
        .toArray()
      : [dragItem.itemId];
  },

  getDragStartParams(_props, _dragItem, _positions, _dropTarget) {
    return {};
  },

  onHover: (_props, _dragItem, _positions, _dropTarget) => {},

  onDrop: (_props, _dragItem, _positions, _dropTarget) => {},

  getDropPreviewForItem: (_props, _dragItem, _dropTarget) => {
    return null;
  }
};

class SearchPanel extends React.PureComponent {
  constructor(props, context) {
    super(props, context);
    this.onReportDimensions = this.onReportDimensions.bind(this);
    this.onSearchItemClick = this.onSearchItemClick.bind(this);
    this.onSearchItemDoubleClick = this.onSearchItemDoubleClick.bind(this);
    this.itemDimensions = {};
  }

  componentDidMount() {
    this.props.registerDropTarget(this, dropCallbacks);
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.matchingItems !== this.props.matchingItems) {
      this._lastClickItemId = null;
      this._firstShiftClickSelection = null;
    }
  }

  onReportDimensions(itemId, rect) {
    this.itemDimensions[itemId] = {
      top: rect.top,
      bottom: rect.bottom
    };
  }

  onSearchItemClick(itemId, modifiers = {}) {
    if (modifiers.SHIFT && this._lastClickItemId) {
      // If this is the first shift click since some other click, record the current selection
      // as the starting point for any subsequent shift clicks
      if (!this._firstShiftClickSelection) {
        this._firstShiftClickSelection = this.props.selectedItemIds;
      }
      // Select everything from last click to current position
      const a = this.props.matchingItems.findIndex(obj => obj.id === this._lastClickItemId);
      const b = this.props.matchingItems.findIndex(obj => obj.id === itemId);
      const low = Math.min(a, b);
      const high = Math.max(a, b);
      const newIds = this.props.matchingItems
        .slice(low, high + 1)
        .map(obj => obj.id)
        .toArray();
      // Note that we can't simply add to selection, as we may want to select a lesser amount than was
      // selected with the previous shift click.
      // So instead we add this new selection to the original selection before the shift clicks began
      const allIds = this._firstShiftClickSelection.push(...newIds).toArray();
      this.props.onSelectItems(allIds);
      return;
    }

    // If we have a non-shift click, record it so we can use it as the starting point for any subsequent
    // shift clicks
    this._lastClickItemId = itemId;
    // Erase the firt shift click so we start again the next time we do a shift click
    this._firstShiftClickSelection = null;

    // And then go on and handle non-shift clicks normally!
    if (modifiers.CONTROL || modifiers.COMMAND) {
      if (this.props.selectedItemIds.contains(itemId)) {
        this.props.onRemoveFromSelection(itemId);
      } else {
        this.props.onAddToSelection(itemId);
      }
    } else {
      this.props.onSelectItems(itemId);
    }
  }

  onSearchItemDoubleClick(itemId, _modifiers) {
    PubSub.publish(PubSubTopics.scrollToItem, { id: itemId });
    if (this.props.onShowInspector) {
      this.props.onShowInspector();
    }
  }

  render() {
    const style = {};
    //#!if mobile === true
// style.paddingLeft = this.props.paddingLeft;
// style.paddingRight = this.props.paddingRight;
    //#!endif

    return (
      <div className="searchPanel" style={style}>
        <TabLock autoFocus={false}>
          <SearchPanelHeader
            searchValue={this.props.searchValue}
            replaceValue={this.props.replaceValue}
            caseSensitive={this.props.caseSensitive}
            wholeWord={this.props.wholeWord}
            replaceExpanded={this.props.replaceExpanded}
            matchCount={this.props.matchingItems.count()}
            allTypes={this.props.allTypes}
            allFields={this.props.allFields}
            excludedSearchTypes={this.props.excludedSearchTypes}
            excludedSearchFields={this.props.excludedSearchFields}
            onToggleType={this.props.onToggleType}
            onToggleField={this.props.onToggleField}
            onCaseSensitiveChange={this.props.onCaseSensitiveChange}
            onWholeWordChange={this.props.onWholeWordChange}
            onSearchValueChange={this.props.onSearchValueChange}
            toggleReplaceExpanded={this.props.toggleReplaceExpanded}
            onSearchReplaceAll={this.props.onSearchReplaceAll}
            //#!if readonly === true
            onReplaceValueChange={null}
            allowReplace={false}
            //#!else
            /* eslint-disable react/jsx-no-duplicate-props */
/* onReplaceValueChange={this.props.onReplaceValueChange}*/
/* allowReplace={true}*/
            //#!endif
            propertiesById={this.props.propertiesById}
          />
          {this.props.searchValue ? (
            <SearchResultList
              matchingItems={this.props.matchingItems}
              selectedItemIds={this.props.selectedItemIds}
              onItemClick={this.onSearchItemClick}
              onItemDoubleClick={this.onSearchItemDoubleClick}
              width={this.props.containerRect.width}
              viewId={this.props.viewId}
            />
          ) : (
            <EmptyState svg="search" type="sidePanel">
              <div key="content">
                <p>Search for events, people, and other entities.</p>
              </div>
            </EmptyState>
          )}
        </TabLock>
      </div>
    );
  }
}

const mapStateToProps = state => {
  return {
    viewId: SEARCH_LIST_VIEW_ID,
    searchValue: uiStateSelectors.getSearchValue(state.present),
    caseSensitive: uiStateSelectors.getSearchCaseSensitive(state.present),
    wholeWord: uiStateSelectors.getSearchWholeWord(state.present),
    replaceExpanded: uiStateSelectors.getSearchReplaceExpanded(state.present),
    replaceValue: uiStateSelectors.getReplaceValue(state.present),
    matchingItems: selectors.getDisplayItemsMatchingSearch(state.present),
    selectedItemIds: uiStateSelectors.getSelectedItems(state.present),
    allTypes: typeSelectors.getOrderedTypes(state.present),
    allFields: allSearchableFields,
    excludedSearchTypes: appStateSelectors.getExcludedSearchTypes(state.present),
    excludedSearchFields: uiStateSelectors.getExcludedSearchFields(state.present),
    propertiesById: definitionSelectors.getPropertiesById(state.present)
  };
};

const readonlyActions = {
  onCaseSensitiveChange: uiStateOperations.changeSearchCaseSensitive,
  onWholeWordChange: uiStateOperations.changeSearchWholeWord,
  onSearchValueChange: uiStateOperations.changeSearchValue,
  toggleReplaceExpanded: value => {
    if (value) {
      return uiStateOperations.expandSearchReplace();
    } else {
      return uiStateOperations.collapseSearchReplace();
    }
  },
  onToggleType: appStateOperations.changeSearchForType,
  onToggleField: uiStateOperations.changeSearchInField,
  onSelectItems: itemIds => {
    let action;
    //#!if harvard === true
    action = batchUtils.batchWithAppendedActions(
      appStateOperations.selectItems(itemIds),
      uiStateOperations.showInspectorModal()
    );
    //#!else
// action = appStateOperations.selectItems(itemIds);
    //#!endif
    return action;
  },
  onAddToSelection: appStateOperations.addItemsToSelection,
  onRemoveFromSelection: appStateOperations.removeItemsFromSelection,
  onShowInspector: uiStateOperations.showInspectorModal
};

const editingActions = {
  onReplaceValueChange: uiStateOperations.changeReplaceValue,
  onSearchReplaceAll: itemOperations.searchReplaceAll,
  onShowInspector: appStateOperations.showRightPanel
};

const mapDispatchToProps = dispatch => mergeDispatchToProps(readonlyActions, editingActions, dispatch);

SearchPanel.propTypes = {
  searchValue: PropTypes.string,
  replaceValue: PropTypes.string,
  caseSensitive: PropTypes.bool.isRequired,
  wholeWord: PropTypes.bool.isRequired,
  replaceExpanded: PropTypes.bool.isRequired,
  matchingItems: ImmutablePropTypes.listOf(PropTypes.instanceOf(immutableRecords.SearchResultRecord)),
  selectedItemIds: ImmutablePropTypes.listOf(PropTypes.string),
  onCaseSensitiveChange: PropTypes.func.isRequired,
  onWholeWordChange: PropTypes.func.isRequired,
  onSearchValueChange: PropTypes.func.isRequired,
  onReplaceValueChange: PropTypes.func,
  toggleReplaceExpanded: PropTypes.func.isRequired,
  onSearchReplaceAll: PropTypes.func,
  onSelectItems: PropTypes.func.isRequired,
  onAddToSelection: PropTypes.func.isRequired,
  onRemoveFromSelection: PropTypes.func.isRequired,

  viewId: PropTypes.string.isRequired,

  allTypes: PropTypeShapes.itemTypeList.isRequired,
  allFields: ImmutablePropTypes.map.isRequired,
  excludedSearchTypes: ImmutablePropTypes.listOf(PropTypes.string).isRequired,
  excludedSearchFields: ImmutablePropTypes.listOf(PropTypes.string).isRequired,
  onToggleType: PropTypes.func.isRequired,
  onToggleField: PropTypes.func.isRequired,
  onShowInspector: PropTypes.func,
  propertiesById: ImmutablePropTypes.map.isRequired,

  // From asDropTarget
  registerDropTarget: PropTypes.func.isRequired,

  // From containerContext
  containerRect: PropTypeShapes.flatRect.isRequired,
  paddingLeft: PropTypes.number,
  paddingRight: PropTypes.number
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withContainerContext(asDropTarget(SearchPanel, SEARCH_RESULTS_VIEW)));
